// InlineEditable — 인라인 텍스트 편집 wrapper.
// 값 표시 상태와 input 편집 상태를 전환한다.
// 저장 중 spinner 표시, 저장 완료/실패 시 외부에서 toast를 호출한다.
//
// 사용 예:
// <InlineEditable
//   value={node.name}
//   onSave={async (v) => api.update(id, { name: v })}
//   validate={(v) => v.trim().length > 0}
//   autoFocus={true}
//   onCancel={() => setEditing(false)}
// />

import {
  useState,
  useRef,
  useEffect,
  type KeyboardEvent,
  type FocusEvent,
} from 'react';
import { Loader2 } from 'lucide-react';

export interface InlineEditableProps {
  /** 현재 표시 값 */
  value: string;
  /**
   * 저장 콜백 — async 가능.
   * resolve: 저장 성공 → 편집 모드 종료.
   * reject: 저장 실패 → 에러 상태 유지, 편집 모드 유지.
   */
  onSave: (newValue: string) => Promise<void> | void;
  /** 취소(Esc / 외부 blur 이후 유효하지 않을 때) 콜백 */
  onCancel?: () => void;
  /** 입력값 유효성 검사 — false 반환 시 저장 불가 */
  validate?: (value: string) => boolean;
  /** 마운트 즉시 포커스 */
  autoFocus?: boolean;
  /** input placeholder */
  placeholder?: string;
  /** 추가 className */
  className?: string;
}

/**
 * InlineEditable — input 한 줄 인라인 편집 컴포넌트.
 * Enter / blur → 저장 시도. Esc → 취소.
 * 저장 중에는 input disabled + Loader2 스피너 표시.
 */
export function InlineEditable({
  value,
  onSave,
  onCancel,
  validate,
  autoFocus = false,
  placeholder = '',
  className = '',
}: InlineEditableProps) {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 마운트 시 포커스 + 전체 선택
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [autoFocus]);

  // 저장 실행 (Enter / blur 공통)
  const commit = async () => {
    const trimmed = draft.trim();

    // 빈 값 → 저장 없이 취소 (빨간 테두리 없음)
    if (!trimmed) {
      onCancel?.();
      return;
    }

    // 유효성 검사 실패 (중복·길이 초과 등 명시적 오류) → 에러 표시
    if (validate && !validate(trimmed)) {
      setError(true);
      return;
    }

    // 변경 없으면 그냥 취소
    if (trimmed === value.trim()) {
      onCancel?.();
      return;
    }

    setSaving(true);
    setError(false);
    try {
      await onSave(trimmed);
      // 저장 성공 시 부모가 unmount/re-render 처리
    } catch {
      // 저장 실패: 에러 상태 유지, 편집 모드 유지
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel?.();
    }
  };

  // blur 시 저장 — 저장 중이면 중복 호출 방지
  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    if (saving) return;
    void commit();
  };

  const classes = [
    'inline-editable',
    error ? 'inline-editable--error' : '',
    saving ? 'inline-editable--saving' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes}>
      <input
        ref={inputRef}
        className="inline-editable__input"
        type="text"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(false);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={saving}
        placeholder={placeholder}
        // 드래그 이벤트가 DnD 시스템으로 올라가지 않도록 차단
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        aria-label="이름 편집"
      />
      {/* 저장 중 스피너 */}
      {saving && (
        <Loader2
          size={12}
          className="inline-editable__spinner animate-spin"
          aria-label="저장 중"
        />
      )}
    </span>
  );
}
